import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useReduxAuth';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_FLOW_TAGS,
  GET_FLOW_ITEM_TAGS,
} from '../graphql/queries/reportFlowTags';
import {
  CREATE_FLOW_TAG,
  UPDATE_FLOW_TAG,
  DELETE_FLOW_TAG,
  ASSIGN_TAGS_TO_ITEM,
  REMOVE_ALL_TAGS_FROM_ITEM,
} from '../graphql/mutations/reportFlowTags';

// Bir akisin etiket setini + akistaki her kartin etiket iliskisini yonetir.
// - `tags`: bu akisa ait tanimli etiketler ({ id, name, color })
// - `itemTagMap`: item.id -> [{ id, name, color }]
// Akis degistiginde yeniden fetch eder. flowId null'ken hicbir sey yapmaz.
const useReportFlowTags = (flowId) => {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [itemTagMap, setItemTagMap] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!flowId) {
      setTags([]);
      setItemTagMap({});
      return;
    }
    setLoading(true);
    try {
      const [tagsData, itemTagsData] = await Promise.all([
        hasuraClient.query(GET_FLOW_TAGS, { flowId }),
        hasuraClient.query(GET_FLOW_ITEM_TAGS, { flowId }),
      ]);
      setTags(tagsData?.report_flow_tags || []);
      const map = {};
      for (const row of itemTagsData?.report_flow_item_tags || []) {
        const list = map[row.item_id] || (map[row.item_id] = []);
        if (row.tag) list.push(row.tag);
      }
      setItemTagMap(map);
    } catch (err) {
      console.error('useReportFlowTags fetchAll error:', err);
      setTags([]);
      setItemTagMap({});
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const createTag = useCallback(async ({ name, color }) => {
    if (!flowId || !user) return null;
    try {
      const res = await hasuraClient.mutate(CREATE_FLOW_TAG, {
        flowId,
        name: name.trim(),
        color,
        companyId: user.companyId,
        createdBy: user.id,
      });
      const created = res?.insert_report_flow_tags_one;
      if (created) setTags((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      return created;
    } catch (err) {
      console.error('createTag error:', err);
      throw err;
    }
  }, [flowId, user]);

  const updateTag = useCallback(async ({ id, name, color }) => {
    try {
      const res = await hasuraClient.mutate(UPDATE_FLOW_TAG, {
        id, name: name.trim(), color,
      });
      const updated = res?.update_report_flow_tags_by_pk;
      if (updated) {
        setTags((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated } : t)));
        // Kart iliskilerinde de gorunen isim/renk guncellenir
        setItemTagMap((prev) => {
          const next = {};
          for (const [itemId, list] of Object.entries(prev)) {
            next[itemId] = list.map((t) => (t.id === id ? { ...t, ...updated } : t));
          }
          return next;
        });
      }
      return updated;
    } catch (err) {
      console.error('updateTag error:', err);
      throw err;
    }
  }, []);

  const deleteTag = useCallback(async (id) => {
    try {
      await hasuraClient.mutate(DELETE_FLOW_TAG, { id });
      setTags((prev) => prev.filter((t) => t.id !== id));
      setItemTagMap((prev) => {
        const next = {};
        for (const [itemId, list] of Object.entries(prev)) {
          next[itemId] = list.filter((t) => t.id !== id);
        }
        return next;
      });
    } catch (err) {
      console.error('deleteTag error:', err);
      throw err;
    }
  }, []);

  // Karttaki etiket setini tam olarak `tagIds`'a esitler: onceden atanmis
  // hepsini siler, sonra yeni listeyi ekler. UI icin en basit "kaydet"
  // pattern'i — kullanici checkbox listesinden secip kaydet.
  const setItemTags = useCallback(async (itemId, tagIds) => {
    if (!user) return;
    try {
      await hasuraClient.mutate(REMOVE_ALL_TAGS_FROM_ITEM, { itemId });
      if (Array.isArray(tagIds) && tagIds.length > 0) {
        const rows = tagIds.map((tagId) => ({
          item_id: itemId,
          tag_id: tagId,
          created_by: user.id,
        }));
        await hasuraClient.mutate(ASSIGN_TAGS_TO_ITEM, { rows });
      }
      // Local state guncelle — tags listesinden ilgili tag objelerini bul.
      const nextList = tags.filter((t) => tagIds.includes(t.id));
      setItemTagMap((prev) => ({ ...prev, [itemId]: nextList }));
    } catch (err) {
      console.error('setItemTags error:', err);
      throw err;
    }
  }, [tags, user]);

  return {
    tags,
    itemTagMap,
    loading,
    fetchAll,
    createTag,
    updateTag,
    deleteTag,
    setItemTags,
  };
};

export default useReportFlowTags;
export { useReportFlowTags };
